import { Spinner } from '@creditornot/cb-progress';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Brand, Enterprise } from 'apis/enterprise/types';
import { DraftVenue } from 'apis/merchant-onboarding-service/types';
import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { setMetadataResource } from 'core/telemetry';
import { ExperienceEntityType } from 'modules/experience-core';

import { Result } from './Result';
import { EntityTypeFilter, EntityTypeFilterType } from './types';
import VirtualizedList, { createResultSection, VirtualizedSection } from '../VirtualizedList';

type EntityResultsProps = {
  brands: Brand[];
  enterprises: Enterprise[];
  draftVenues?: DraftVenue[];
  isLoading: boolean;
  merchants: Merchant[];
  venues: Venue[];
  filter: EntityTypeFilterType;
  onSelect?: () => void;
};

const StyledLoader = styled.div`
  padding-block-start: 8px;
  padding-inline: 16px;
  display: flex;
  justify-content: center;
`;

const Loader = () => (
  <StyledLoader>
    <Spinner size="medium" />
  </StyledLoader>
);

const EntityResultsContainer = styled.div`
  width: 100%;
  height: 600px;
`;

type ResultBuild = {
  type: ExperienceEntityType;
  results: JSX.Element[];
};

export const EntityResults = ({
  brands,
  enterprises,
  draftVenues = [],
  isLoading,
  merchants,
  venues,
  filter,
  onSelect,
}: EntityResultsProps) => {
  const history = useHistory();

  const handleSelect = (slug: string, resultType: ExperienceEntityType, resultId: string = '') => {
    resultId = resultId || slug;
    setMetadataResource(resultId, resultType);
    onSelect?.();
    history.push(`/experience/${resultType.replace('_', '-')}/${slug}`);
  };

  const buildEnterpriseResults = (): ResultBuild => {
    return {
      type: ExperienceEntityType.ENTERPRISE,
      results: enterprises.map(enterprise => (
        <Result
          key={enterprise.id}
          label={enterprise.name}
          onClick={() => handleSelect(enterprise.id, ExperienceEntityType.ENTERPRISE)}
        />
      )),
    };
  };

  const buildBrandResults = (): ResultBuild => {
    return {
      type: ExperienceEntityType.BRAND,
      results: brands.map(brand => {
        return (
          <Result
            key={brand.id}
            label={brand.name}
            subLabel={brand.country}
            onClick={() => handleSelect(brand.id, ExperienceEntityType.BRAND)}
          />
        );
      }),
    };
  };

  const buildMerchantResults = (): ResultBuild => {
    return {
      type: ExperienceEntityType.MERCHANT,
      results: merchants.map(merchant => {
        const slug = merchant.itemid.$oid || '';
        return (
          <Result
            key={slug}
            label={merchant.name[0].value}
            onClick={() => handleSelect(slug, ExperienceEntityType.MERCHANT)}
          />
        );
      }),
    };
  };

  const buildDraftVenueResults = (): ResultBuild => {
    return {
      type: ExperienceEntityType.VENUE,
      results: draftVenues.map(venue => (
        <Result key={venue.id} label={venue.name} subLabel={venue.address} tag="PENDING APPROVAL" disabled />
      )),
    };
  };

  const buildVenueResults = (): ResultBuild => {
    return {
      type: ExperienceEntityType.VENUE,
      results: venues.map(venue => {
        const slug = venue.slug || '';
        return (
          <Result
            key={slug}
            label={venue.name[0].value}
            onClick={() => handleSelect(slug, ExperienceEntityType.VENUE, venue.id)}
            subLabel={venue.address}
            listImage={venue.listimage || undefined}
          />
        );
      }),
    };
  };

  const calculateResults = (filter: EntityTypeFilterType): ResultBuild[] => {
    switch (filter) {
      case EntityTypeFilter.ALL:
        return [
          buildEnterpriseResults(),
          buildBrandResults(),
          buildMerchantResults(),
          {
            type: ExperienceEntityType.VENUE,
            results: [...buildDraftVenueResults().results, ...buildVenueResults().results],
          },
        ];
      case EntityTypeFilter.ENTERPRISE:
        return [buildEnterpriseResults()];
      case EntityTypeFilter.BRAND:
        return [buildBrandResults()];
      case EntityTypeFilter.MERCHANT:
        return [buildMerchantResults()];
      case EntityTypeFilter.VENUE:
        return [
          {
            type: ExperienceEntityType.VENUE,
            results: [...buildDraftVenueResults().results, ...buildVenueResults().results],
          },
        ];
    }
  };
  const results = calculateResults(filter);

  const shouldShowResults =
    !!merchants.length || !!draftVenues.length || !!venues.length || !!enterprises.length || !!brands.length;

  const virtualizedSections = useMemo((): VirtualizedSection[] => {
    const entityTypeHeaderMappings = {
      [ExperienceEntityType.ENTERPRISE]: 'experience.enterprises',
      [ExperienceEntityType.BRAND]: 'experience.brands',
      [ExperienceEntityType.MERCHANT]: 'experience.merchants',
      [ExperienceEntityType.VENUE]: 'experience.venues',
    };
    const sections = results.map(resultBuild =>
      createResultSection(resultBuild.results, entityTypeHeaderMappings[resultBuild.type]),
    );

    return sections.filter((section): section is VirtualizedSection => !!section);
  }, [results]);

  return (
    <EntityResultsContainer>
      {isLoading && <Loader />}
      {!isLoading && shouldShowResults && <VirtualizedList sections={virtualizedSections} key={filter} />}
    </EntityResultsContainer>
  );
};
